import moment from 'moment';
import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Loader } from 'semantic-ui-react';

import analytics from '../../../shared/services/analytics/analytics';
import { AlphasAddToListContainer } from '../../../containers/AlphasAddToList';
import PerformanceComparison from '../../../containers/PerformanceComparison/PerformanceComparison';
import SelectedAlphas from '../../../containers/SelectedAlphas/SelectedAlphas';
import { localeData } from '../../../locales/data';
import { addModifiers } from '../../../shared/helpers';
import { formatMessageHelper, FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import ChartContainer from '../../AlphaChart/ChartContainer';
import AlphaCodeContainer from '../../AlphaCode/AlphaCodeContainer';
import AlphaCorrelation from '../../AlphaCorrelation/AlphaCorrelation';
import { AlphaProperties } from '../../AlphaProperties';
import AlphaSummary from '../../AlphaSummary/AlphaSummary';
import AlphaTestingStatus from '../../AlphaTestingStatus/AlphaTestingStatus';
import { Container } from '../../Container';
import FlashMessages from '../../FlashMessages/FlashMessages';
import { HOCPanel } from '../../Panel/Panel';
import { FooterAlphasDetails } from '../AlphaFooter/AlphaFooter';
import noop from 'lodash/noop';

import './AlphaPage.scss';
import { withAlphaChartData } from '../../AlphaChart/HOCAlphaChart';

const AlphaChart = React.memo(withAlphaChartData(ChartContainer)) as React.ComponentType<any>;

interface AlphaHeaderProps {
  alphaDetail: any;
  handleVisualization: () => void;
  handleFavorite: () => void;
  isAlphaCreatedByTheCurrentUser: boolean;
  intl: any;
}

export interface AlphaPageProps {
  isFetching: boolean;
  alphaDetail: any;
  handleVisualization: () => void;
  handleFavorite: () => void;
  handleCheckSubmission: () => void;
  alphaStatus: string;
  handleSubmitAlpha: () => void;
  fetchingCheck: boolean;
  hasErrors?: boolean;
  isAlphaCreatedByTheCurrentUser: boolean;
}

const Panel = HOCPanel();

const AlphaHeader = ({ alphaDetail, handleFavorite, handleVisualization, isAlphaCreatedByTheCurrentUser, intl }: AlphaHeaderProps) => {
  let dateCreated: string = '';
  let title = 'anonymous';

  if (alphaDetail) {
    dateCreated = moment(alphaDetail.get('dateCreated')).tz('America/New_York').format('L z');

    title = alphaDetail.get('name') ?? title;
  }

  const alphaStatus = alphaDetail?.get('status') ?? 'unsubmitted';
  const lowerCaseStatus = alphaStatus?.toLowerCase();
  const modifiers = lowerCaseStatus ? [lowerCaseStatus] : [];
  const stage = alphaStatus ?? null;

  return (
    <header className="alpha__header">
      <div className="alpha__header-logo">
        {stage ? (
          <div className={addModifiers('alpha__header-icon', modifiers)}>
            <FormattedMessageHelper
              tagName="span"
              id={`alphasDetails.status.${lowerCaseStatus}`}
              defaultMessage={localeData.en[`alphasDetails.status.${lowerCaseStatus}`]}
            />
          </div>
        ) : null}

        <div className="alpha__header-date">
          <FormattedMessageHelper
            tagName="span"
            id="alphaPage.alphaHeader.created"
            defaultMessage={localeData.en['alphaPage.alphaHeader.created']}
          />
          &nbsp;
          {dateCreated}
        </div>
      </div>
      <div className="alpha__header-title">{title}</div>
      <div className="alpha__header-actions">
        {alphaDetail && (
          <AlphasAddToListContainer
            alphasData={[alphaDetail.get('id')]}
            btnLabel={formatMessageHelper(intl, 'alphasAddToList.addAlphaToAList')}
            btnClasses="alpha__header-add"
            disabled={!isAlphaCreatedByTheCurrentUser}
            // TODO(BRAINTECH-5372): confirm event_label — Open Question #6 in
            // the implementation plan suggests 'single' for the standalone
            // alpha-page caller; tentatively using 'single' until product
            // confirmation.
            onTrackOpen={() => analytics.trackCustomEvent('alphas_click_add_to_list', { event_label: 'single' })}
          />
        )}
        <span
          className={addModifiers(alphaDetail?.get('favorite') ? 'alpha__header-star--selected' : 'alpha__header-star', [
            !isAlphaCreatedByTheCurrentUser ? 'disabled' : '',
          ])}
          onClick={!isAlphaCreatedByTheCurrentUser ? noop : handleFavorite}
        />
        <span
          className={addModifiers(alphaDetail?.get('hidden') ? 'alpha__header-visualization' : 'alpha__header-visualization--selected', [
            !isAlphaCreatedByTheCurrentUser ? 'disabled' : '',
          ])}
          onClick={!isAlphaCreatedByTheCurrentUser ? noop : handleVisualization}
        />
        {isAlphaCreatedByTheCurrentUser && <span className="alpha__header-settings" />}
      </div>
    </header>
  );
};

function NoAlpha() {
  return (
    <Container modifiers={['no-alpha']}>
      <Panel hasHeader={false}>
        <div className="alpha__no-match">
          <FormattedMessageHelper id="alphaPage.noResults" />
        </div>
      </Panel>
    </Container>
  );
}

export const AlphaPageIntl = ({
  isFetching,
  alphaDetail,
  handleFavorite,
  handleVisualization,
  fetchingCheck,
  alphaStatus,
  hasErrors,
  handleCheckSubmission,
  handleSubmitAlpha,
  intl,
  isAlphaCreatedByTheCurrentUser,
}: AlphaPageProps & InjectedIntlProps) => {
  const hasFailedChecks = alphaDetail?.getIn(['is', 'checks'])?.filter((check: any) => check.get('result') === 'FAIL').size > 0;
  const isTestPeriodControlShown = alphaDetail?.getIn(['test', 'startDate']) && alphaDetail?.get('status') === 'UNSUBMITTED';
  const isTestPeriodShown = alphaDetail?.get('isTestPeriodShown');
  const isTestPeriodHidden = isTestPeriodControlShown && !isTestPeriodShown;
  const customNavigatorSize = 167; //experimentally found value, works for alpha screen

  const path = ['alphas'];

  const author = alphaDetail?.get('author');

  if (isFetching) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  }

  if (alphaDetail === null) {
    return <NoAlpha />;
  }

  return (
    <section className="alpha">
      <AlphaHeader
        alphaDetail={alphaDetail}
        handleFavorite={handleFavorite}
        handleVisualization={handleVisualization}
        intl={intl}
        isAlphaCreatedByTheCurrentUser={isAlphaCreatedByTheCurrentUser}
      />
      <div className="alpha__content">
        <div className="alpha__column">
          <div className="alpha__code">{alphaDetail && <AlphaCodeContainer />}</div>
          <div className="alpha__chart">
            {alphaDetail && (
              <AlphaChart
                path={path}
                id="alphas-chart"
                modifiers={['alphas-details']}
                fatherClass=".alpha__chart"
                position="odd"
                customNavigatorSize={customNavigatorSize}
              />
            )}
          </div>
          <div>{!isTestPeriodHidden && <AlphaTestingStatus path={path} title={'Testing Status'} />}</div>
          {alphaDetail && author && alphaDetail.get('type') === 'SUPER' && <SelectedAlphas path={path} id="alphas-selectedAlphas" />}
        </div>
        <div className="alpha__column">
          <div className="alpha__summary">
            {alphaDetail && (
              <AlphaSummary
                path={path}
                renderGrade={true}
                renderTheme={true}
                renderClassification={true}
                renderPyramids={true}
                title={'Summary'}
              />
            )}
          </div>
          <div id="alphas-correlation" className="alpha__is-correlation">
            {alphaDetail && !isTestPeriodHidden && author && <AlphaCorrelation path={path} />}
          </div>
          <div className="alpha__is-performanceComparison">
            {alphaDetail && !isTestPeriodHidden && author && alphaDetail.get('stage') === 'IS' && (
              <PerformanceComparison path={path} id="alphas-performanceComparison" isSimulation={false} />
            )}
          </div>
          <div className="alpha__properties">{alphaDetail && author && <AlphaProperties path={path} />}</div>
        </div>
      </div>
      {isAlphaCreatedByTheCurrentUser && (
        <div className="alphas__message">
          <FlashMessages location="alpha-details" />
        </div>
      )}
      {author && (
        <FooterAlphasDetails
          handleCheckSubmission={handleCheckSubmission}
          alphaStatus={alphaStatus}
          handleSubmitAlpha={handleSubmitAlpha}
          fetchingCheck={fetchingCheck}
          hasFailedChecks={alphaDetail?.getIn(['is', 'checks']) ? hasFailedChecks : false}
          isTestPeriodControlShown={isTestPeriodControlShown}
          isTestPeriodShown={isTestPeriodShown}
          hasErrors={hasErrors}
          isAlphaCreatedByTheCurrentUser={isAlphaCreatedByTheCurrentUser}
        />
      )}
    </section>
  );
};

export const AlphaPage = injectIntl(AlphaPageIntl);
